import { motion, useReducedMotion, Variants } from "framer-motion";
import { ArrowRight, DoorOpen } from "lucide-react";

import { ResourceView } from "@/components/routing/resource-views";
import { cn } from "@/lib/utils";
import { GuardStatePanel } from "./GuardStatePanel";

interface OutrasVisoesPanelProps {
  /** Rótulo da visão que o link pedia e a pessoa não pode abrir. */
  visaoLabel: string;
  alternativas: readonly ResourceView[];
  onAbrir: (view: ResourceView) => void;
}

const card: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.07, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  }),
};

export function OutrasVisoesPanel({
  visaoLabel,
  alternativas,
  onAbrir,
}: Readonly<OutrasVisoesPanelProps>) {
  const semMovimento = useReducedMotion();

  return (
    <GuardStatePanel
      icon={DoorOpen}
      tone="warning"
      title="Você tem outra visão deste registro"
      description={
        <>
          O link aponta para a visão de{" "}
          <strong className="font-medium text-foreground">{visaoLabel}</strong>, que seu perfil não
          acessa. É o mesmo registro — escolha por onde abrir:
        </>
      }
    >
      <nav
        aria-label="Outras visões deste registro"
        className={cn(
          "grid gap-3",
          alternativas.length > 1 ? "sm:grid-cols-2" : "mx-auto max-w-sm grid-cols-1",
        )}
      >
        {alternativas.map((view, i) => (
          <motion.button
            key={view.key}
            type="button"
            custom={i}
            variants={card}
            initial="hidden"
            animate="show"
            whileHover={semMovimento ? undefined : { y: -3 }}
            whileTap={{ scale: 0.985 }}
            onClick={() => onAbrir(view)}
            className={cn(
              "group relative flex items-center gap-4 overflow-hidden rounded-xl border border-border/60 bg-card/60 p-4 text-left",
              "shadow-sm backdrop-blur-sm transition-colors hover:border-amber-500/40 hover:bg-card",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            )}
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 ring-1 ring-inset ring-amber-500/20">
              <view.icon className="h-5 w-5 text-amber-500" strokeWidth={1.75} />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">{view.label}</span>
              <span className="block text-xs leading-snug text-muted-foreground">
                {view.descricao}
              </span>
            </span>

            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-amber-500" />
          </motion.button>
        ))}
      </nav>
    </GuardStatePanel>
  );
}
